import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgbDropdown, NgbDropdownAnchor } from '@ng-bootstrap/ng-bootstrap';
import {
  MultiselectOptions,
  SelectModel,
  SelectValue,
} from './select.model';
import { ArrayHelper } from '../../services/core';
import { isEqual, isEmpty } from 'lodash-es';

@Component({
  selector: 'oir-multiselect',
  templateUrl: './multiselect.component.html',
  styleUrls: ['./multiselect.component.less'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiselectComponent),
      multi: true,
    },
  ],
})
export class MultiselectComponent implements OnInit, OnChanges {
  @ViewChildren('inputs') inputs: QueryList<ElementRef>;
  @ViewChildren('searchInput') searchInputs: QueryList<ElementRef>;

  @ViewChild(NgbDropdown, { static: true }) dropdown: NgbDropdown;
  @ViewChild(NgbDropdownAnchor) ddt: NgbDropdownAnchor;
  @ViewChild('list') list: ElementRef;

  @Input() placeholder: string;
  @Input() data: SelectValue<any>[];
  @Input() options: MultiselectOptions;
  @Input() disabled: boolean;
  @Input() id = '';

  @Input() model: any = [];
  @Output() readonly modelChange: EventEmitter<any> = new EventEmitter();
  maxHeightPx = 508;
  maxHeight = this.maxHeightPx;

  dataObj: SelectValue<any>[] = [];
  query: string;
  title = '';
  multiselectModel: SelectModel = {
    headings: [],
    active: false,
    animating: false
  };

  private init = false;
  private clickEventListener: () => void;
  private keyDownEventListener: () => void;

  propagateChange = (_: any) => {};
  propagateTouch = (_?: any) => {};

  constructor(
    private readonly el: ElementRef,
    private readonly renderer: Renderer2,
    private readonly cd: ChangeDetectorRef
  ) {}

  get activePills(): any {
    return this.dataObj.filter((x) => x.selected);
  }

  ngOnInit(): void {
    this.dropdown.placement = 'bottom';
    this.dropdown.autoClose = false;

    this.dataObj = JSON.parse(JSON.stringify(this.data || []));

    this.initOptions();

    if (!this.placeholder) {
      this.placeholder = 'Select an option';
    }
    if (!this.dataObj) {
      this.dataObj = [];
    }
    if (this.dataObj.length > 0) {
      this.refreshModel();
    }

    this.init = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.init) {
      if (changes.data && this.data && !isEqual(changes.data.currentValue, changes.data.previousValue)) {
        this.dataObj = JSON.parse(JSON.stringify(this.data));
        this.refreshModel();
      }
      if (
        changes.placeholder && changes.placeholder.previousValue !== changes.placeholder &&
        changes.data && !isEmpty(changes.data.currentValue)
      ) {
        this.title = changes.placeholder.currentValue;
      }
      if (changes.model && this.model && !isEqual(changes.model.currentValue, changes.model.previousValue)) {
        this.refreshModel();
      }
    }
  }

  public onKeyPressMove(keyPressEvent: KeyboardEvent): void {
    const list = this.list.nativeElement.querySelectorAll('button[role="option"]');
    switch (keyPressEvent.code) {
      case 'ArrowUp':
        !this.dropdown.isOpen() ? this.dropdown.open() : list[0].focus();
        keyPressEvent.preventDefault();
        break;
      case 'ArrowDown':
        !this.dropdown.isOpen() ? this.dropdown.open() : list[0].focus();
        keyPressEvent.preventDefault();
        break; 
      case 'Home':     
      case 'End':
        keyPressEvent.preventDefault();
        break;
      case 'Enter':
      case 'Space':
        this.dropdown.isOpen() ? this.dropdown.open(): this.dropdown.close();      
        keyPressEvent.preventDefault();
        break;
      case 'Escape': 
        this.dropdown.close();  
        keyPressEvent.preventDefault();
        break;
    }
  }

  writeValue(obj: any): void {
    obj = obj || [];
    if (this.init) {
      // eslint-disable-next-line no-prototype-builtins
      this.model = obj.hasOwnProperty('value') ? obj.value : obj;

      this.refreshModel();
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  setDisabledState(value: any): void {
    this.disabled = value;
  }

  displayTitle(): boolean {
    return (
      !this.options.pills ||
      (this.options.pills && this.model && this.model.length === 0)
    );
  }

  selectKeyDown(event: KeyboardEvent): void {
    if (event.which === 13 || event.which === 32) {
      this.dropdown.toggle();
      if (this.options.search && this.dropdown.isOpen()) {
        if (!this.options.headings) {
          this.setFocusOnSearchBox(0);
        } else {
          this.setFocusOnSearchBox(1);
        }
      }
    } else if (event.which === 46) {
      this.clearSelected();
    }
  }

  setFocusOnSearchBox(num: number): void {
    setTimeout(() => {
      this.searchInputs.toArray()[num].nativeElement.focus();
    }, 100);
  }

  clearSearchInput(): void {
    this.searchInputs.toArray().forEach((el) => {
      el.nativeElement.value = '';
    });
    if (this.query !== '') {
      this.query = '';
      this.searchValue();
    }
  }

  dropdownToggled(): void {
    if (!this.dropdown.isOpen()) {
      // Clears search option.
      this.clearSearchInput();

      // Removes listener if dropdown closed.
      this.clickEventListener();
      this.keyDownEventListener();
    } else {
      // On dropdown open add event listener.
      this.registerListener();
    }

    const ddBounds = this.ddt.nativeElement.getBoundingClientRect();
    const body = document.querySelector('#body.body') as HTMLElement;
    // Get visible window port height and minus the element height and distance to bottom of viewport.
    if (body && !this.options.maxHeight) {
      const maxHeight = body.offsetHeight - (ddBounds.top + ddBounds.height);
      this.maxHeight =
        maxHeight < this.maxHeightPx ? maxHeight : this.maxHeightPx;
    } else if (this.options.maxHeight) {
      this.maxHeight = this.options.maxHeight;
    }
  }

  toggle(): void {
    if (!this.disabled && !this.multiselectModel.animating) {
      this.query = '';
      this.searchValue();

      this.multiselectModel.active = !this.multiselectModel.active;
      this.multiselectModel.animating = true;

      setTimeout(() => (this.multiselectModel.animating = false), 280);
    }
  }

  select(option: SelectValue<any>): void {
    if (!option.disabled) {
      if (option.selected) {
        option.selected = false;
        this.model =
          this.model.filter((x: SelectValue<any>) => x !== option.value) || [];
      } else {
        this.dataObj.filter((x) => x.value === option.value)[0].selected = true;
        this.model.push(option.value);

        // Angular change detection doesn't check the contents of arrays or object.
        this.model = this.model.slice();
      }

      this.refreshTitle();
      this.modelChange.emit(this.model);
      this.propagateChange(this.model);
    }
  }

  selectAllValues(): void {
    for (const dataItem of this.dataObj) {
      if (!dataItem.hidden && !dataItem.disabled) {
        dataItem.selected = true;
      }
    }

    this.model = this.dataObj.filter((x) => x.selected).map((x) => x.value);

    this.refreshTitle();

    this.propagateChange(this.model);
    this.modelChange.emit(this.model);
  }

  clearSelected(): void {
    // const selected = this.dataObj.filter(x => x.selected && !x.hidden);
    for (const dataItem of this.dataObj) {
      if (!dataItem.hidden) {
        dataItem.selected = false;
      }
    }

    this.model = [];

    this.refreshTitle();

    this.propagateChange(this.model);
    this.modelChange.emit(this.model);
  }

  close(): void {
    if (this.multiselectModel.active) {
      this.multiselectModel.active = false;
      this.multiselectModel.animating = true;

      setTimeout(() => (this.multiselectModel.animating = false), 280);

      this.propagateTouch();
    }
  }

  removePill($event: any, option: SelectValue<any>): void {
    if (!this.disabled) {
      option.selected = false;

      this.model = this.model.filter(
        (x: SelectValue<any>) => JSON.stringify(x) !== JSON.stringify(option.value)
      );
      const itemRemoved = this.dataObj.filter(
        (x) => x.value === option.value
      )[0];
      itemRemoved.selected = false;

      this.refreshTitle();

      this.modelChange.emit(this.model);
      this.propagateChange(this.model);

      let selectedIndex = 0;

      this.inputs.forEach((child, index) => {
        if (child.nativeElement.textContent === itemRemoved.title) {
          selectedIndex = index;
        }
      });
      if (this.inputs.length > 1) {
        if (selectedIndex - 1 < 0) {
          selectedIndex = 2;
        }
        this.inputs.toArray()[selectedIndex - 1].nativeElement.focus();
      }
      $event.stopPropagation();
    }
  }

  onSearch(): void {
    setTimeout(() => this.searchValue());
  }

  private refreshModel(): void {
    let values: SelectValue<any>[] = [];

    if (this.model) {
      this.clearUnselected(this.model);

      for (const modelItem of this.model) {
        if (typeof modelItem === 'object') {
          if (this.options.modelPropertyName) {
            values = this.dataObj.filter(
              (x) =>
                x.value[this.options.modelPropertyName] ===
                modelItem[this.options.modelPropertyName]
            );
          } else {
            if (modelItem) {
              values = this.dataObj.filter(
                (x) => JSON.stringify(x.value) === JSON.stringify(modelItem)
              );
              console.log(
                'Model Property Name was not provided for Multiselect'
              );
            }
          }
        } else {
          values = this.dataObj.filter((x) => x.value === modelItem) || [];
        }

        if (values.length > 0) {
          values[0].selected = true;
        }
      }
    }

    this.refreshTitle();
  }

  private initOptions(): void {
    this.options =
      this.options || {
        pills: false,
        search: false,
        headings: false,
        selectAll: true,
        modelPropertyName: ''
    };

    if (this.options.headings) {
      this.groupValuesByHeading();
    }
  }

  private clearUnselected(items: Array<any>): void {
    if (this.dataObj.length) {
      this.dataObj
        .filter((x) => items.indexOf(x.value) < 0)
        .forEach((x) => (x.selected = false));
    }
  }

  private groupValuesByHeading(): void {
    const headingsGroup = ArrayHelper.groupBy<SelectValue<any>>(
      this.dataObj,
      'heading'
    );
    for (const headingGroup of headingsGroup) {
      this.multiselectModel.headings.push({ title: headingGroup.key });
    }
  }

  private searchValue(): void {
    if (this.options.search) {
      if (this.query === '' || typeof this.query === 'undefined') {
        for (const dataItem of this.dataObj) {
          dataItem.hidden = false;
        }
      } else {
        const results = this.dataObj.filter(
          (x) => x.title.toLowerCase().indexOf(this.query.toLowerCase()) >= 0
        );
        if (results) {
          for (const dataItem of this.dataObj) {
            dataItem.hidden = results.indexOf(dataItem) === -1;
          }
        }
      }
      this.cd.detectChanges();

      if (this.options.headings) {
        this.checkHeadingsVisibility();
      }
    }
  }

  private checkHeadingsVisibility(): void {
    const headingsGroup = ArrayHelper.groupBy<SelectValue<any>>(
      this.dataObj,
      'heading'
    );

    for (const headingGroup of headingsGroup) {
      const hidden =
        headingGroup.values.filter((x: SelectValue<any>) => x.hidden).length ===
        headingGroup.values.length;
      this.multiselectModel.headings.filter(
        (x) => x.title === headingGroup.key
      )[0].hidden = hidden;
    }
  }

  private refreshTitle(): void {
    this.title = this.placeholder;

    if (!this.options.pills && this.model) {
      if (this.model.length === 1) {
        this.title = this.dataObj.filter(
          (x) => x.value === this.model[0]
        )[0].title;
      } else if (this.model.length > 1) {
        this.title = 'Multiple options selected';
      }
    }
  }

  private registerListener(): void {
    // If clicked outside of the component close dropdown if open.
    this.clickEventListener = this.renderer.listen(
      'document',
      'click',
      (event: any) => {
        if (!this.el.nativeElement.contains(event.target)) {
          if (this.dropdown.isOpen) {
            this.dropdown.toggle();
          }
          this.clickEventListener();
        }
      }
    );
    // If keypressed outside of the component close dropdown if open.
    this.keyDownEventListener = this.renderer.listen(
      'document',
      'keyup',
      (event: any) => {
        if (!this.el.nativeElement.contains(event.target)) {
          if (this.dropdown.isOpen) {
            this.dropdown.toggle();
          }
          this.keyDownEventListener();
        }
      }
    );
  }
}
